import { zodResolver } from '@hookform/resolvers/zod';
import { Check, Clock, LinkExternal01, Zap } from '@timelyapp/icons/solid-icons';
import {
  type AiPreferences,
  type AiPreferencesPresetEnums,
  AiPreferencesSchema,
  aiPreferencesPresetEnums,
} from '@timelyapp/integration-cloud-api-spec';
import {
  Box,
  Button,
  Card,
  CustomIcon,
  Form,
  Message,
  Pill,
  RadixTooltip,
  Settings,
  Text,
} from '@timelyapp/react';
import React, { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { type AiPreferencesViewProps, AiPreferencesViews } from './ai.preferences.types';
import optionImg3 from './option-concise.png';
import optionImg2 from './option-efficient.png';
import optionImg1 from './option-exact.png';

type PresetMeta = Record<
  AiPreferencesPresetEnums,
  {
    key: AiPreferencesPresetEnums;
    title: string;
    image: string;
    icon?: React.ReactNode | React.ReactElement;
    description: string;
    /** When this option is selected, set other properties */
    setOtherValues: Partial<Record<keyof AiPreferences, AiPreferences[keyof AiPreferences]>>;
  }
>;

export const presetMetaMap: PresetMeta = {
  exact: {
    key: 'exact',
    title: 'Exact',
    icon: <Clock />,
    image: optionImg1,
    setOtherValues: {
      fill_gap: 1,
      min_duration: 1,
    },
    description:
      'Precise and unaltered, where every minute is accounted for without any modification.',
  },
  efficient: {
    key: 'efficient',
    title: 'Efficient',
    image: optionImg2,
    icon: <Zap />,
    setOtherValues: {
      fill_gap: 10,
      min_duration: 5,
    },
    description:
      'Efficient and focused timesheet, reducing clutter while retaining important information.',
  },
  concise: {
    key: 'concise',
    title: 'Concise',
    icon: <Check />,
    image: optionImg3,
    setOtherValues: {
      fill_gap: 30,
      min_duration: 10,
    },
    description: 'High-level overview, focusing on the key points and broader trends.',
  },
} as const;

/**
 * Sub-view of AiPreferencesModal
 */
const AiPreferencesGeneralSettings = (props: AiPreferencesViewProps) => {
  const { initialPreferences, onSubmit, onReset } = props;
  const initiallyEnabled = !!initialPreferences?.enabled;

  const handleOnSubmit = useCallback(
    (payload: AiPreferences) => {
      onSubmit?.({ type: 'aiPreferences', payload });
    },
    [onSubmit]
  );

  const {
    register,
    watch,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<AiPreferences>({
    resolver: zodResolver(AiPreferencesSchema),
    defaultValues: initialPreferences || AiPreferencesSchema.parse({}),
  });

  const isEnabled = watch('enabled');
  const wasEnabled = watch('id');
  const suggestNotes = watch('suggest_note');
  const trainTags = watch('train_tags');

  const handlePresetChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const name = event.target.name as 'preset';
      const value = event.target.value as AiPreferencesPresetEnums;
      setValue(name, value, { shouldDirty: true });
      setValue('enabled', true, { shouldDirty: true });
      const newValues = presetMetaMap[value].setOtherValues;
      Object.entries(newValues).forEach(([key, value]) => {
        setValue(key as keyof AiPreferences, value);
      });
    },
    [setValue]
  );

  const handleChange = useCallback(() => {
    const payload = getValues();
    props.onChange?.({ type: 'aiPreferences', payload });
  }, [getValues, props.onChange]);

  const renderMessages = () => {
    if (!isEnabled) {
      return (
        <>
          <Box.Spacer />
          <Message variant="info" size="medium" autoIcon>
            Choose a style to enable Timesheet Assistant.
          </Message>
        </>
      );
    }

    if (isDirty && wasEnabled) {
      return (
        <Message variant="info" size="medium" autoIcon>
          Your Timesheet Assistant may take up to 30 mins to reflect changed preferences.
        </Message>
      );
    }
    if (isDirty) {
      return (
        <Message variant="alert" autoIcon size="medium">
          Your Timesheet Assistant may take up to 30 mins to make suggests. As you log more time,
          additional suggestions will follow, improving as it learns how your Memories connect to
          projects.
        </Message>
      );
    }

    return null;
  };

  return (
    <>
      <Settings.Main.Header>
        <Box.Row gap="medium">
          <Text.Headline size="medium" color="success">
            Timesheet Assistant
          </Text.Headline>
          <Text.Headline color="success">
            <CustomIcon.Sparkling width={40} height={40} />
          </Text.Headline>
          <Box.Spacer />
          {!isEnabled && (
            <Pill variant="alert" size="medium">
              Disabled
            </Pill>
          )}
        </Box.Row>
      </Settings.Main.Header>

      <Form
        style={{ flex: '1 1 auto', overflow: 'hidden' }}
        onSubmit={handleSubmit(handleOnSubmit)}
        onChange={handleChange}
        data-testid="timesheet-assistance-settings"
      >
        <Settings.Main.Content>
          <Box.Column gap="24px">
            <Text.Paragraph size="large">
              Select a timesheet style below to receive AI suggestions based on your logging
              patterns.
            </Text.Paragraph>
            <Box.Grid gap="large" gridTemplateColumns="1fr 1fr 1fr">
              {aiPreferencesPresetEnums.map((presetKey, idx) => {
                const preset = presetMetaMap[presetKey];
                return (
                  <Card.Form.Radio
                    key={idx}
                    {...register('preset')}
                    value={presetKey}
                    onChange={handlePresetChange}
                    data-testid={`timesheet-assistant-settings_preset_${presetKey}`}
                    fancyCheck
                  >
                    <Card.CoverImage>
                      <img
                        src={preset.image}
                        rel="noopener noreferrer"
                        crossOrigin="anonymous"
                        referrerPolicy="no-referrer"
                        alt="A random image"
                      />
                    </Card.CoverImage>
                    <Card.Header>{preset.title}</Card.Header>
                    <Card.Body>
                      <Text.Paragraph size="small">{preset.description}</Text.Paragraph>
                    </Card.Body>
                  </Card.Form.Radio>
                );
              })}
            </Box.Grid>
            {isEnabled && (
              <Box.Column gap="large">
                <Text.Label size="large" data-testid="timesheet-assistant-settings_heading_options">
                  Options
                </Text.Label>

                {/* !! If here to remove, remember to coerce setting off via `coerceAiPreferences` */}
                <Box.Row gap="large">
                  <Form.Label>
                    <Form.Checkbox {...register('train_tags')} />
                    Use tags suggested by AI
                  </Form.Label>
                  {trainTags && (
                    <RadixTooltip tooltip="Allow suggestions to combine with existing time entries, so long as tags don't conflict">
                      <Form.Label size="small">
                        <Form.Checkbox {...register('relaxed_tag_matching')} />
                        Relaxed
                      </Form.Label>
                    </RadixTooltip>
                  )}
                </Box.Row>
                {/* !! If here to remove, remember to coerce setting off via `coerceAiPreferences` */}
                <Box.Row gap="large">
                  <Form.Label>
                    <Form.Checkbox {...register('suggest_note')} />
                    Use notes suggested by AI
                  </Form.Label>
                  {suggestNotes && (
                    <RadixTooltip tooltip="Allow suggestions to combine with existing time entries, even when note differs">
                      <Form.Label size="small">
                        <Form.Checkbox {...register('relaxed_note_matching')} />
                        Relaxed
                      </Form.Label>
                    </RadixTooltip>
                  )}
                </Box.Row>
                <Form.Label>
                  {/* !! If here to remove, remember to coerce setting off via `coerceAiPreferences` */}
                  <Form.Checkbox {...register('show_timesheet_predictions')} />
                  Show suggestions on Day View's timesheet{' '}
                  <Text size="small" color="subtle">
                    (Experimental. Week View not currently supported)
                  </Text>
                </Form.Label>
              </Box.Column>
            )}
            {renderMessages()}
          </Box.Column>
        </Settings.Main.Content>
        <Settings.Main.Footer>
          {isEnabled && initiallyEnabled && (
            <Button
              priority="xlow"
              size="small"
              onClick={() => {
                setValue('enabled', false);
                setValue('preset', null);
                handleSubmit(handleOnSubmit)();
              }}
              data-testid="timesheet-assistant-settings_button_disable"
            >
              <CustomIcon.SparklingOff />
              Disable Timesheet Assistant
            </Button>
          )}
          <Button
            priority="xlow"
            size="small"
            asChild
            data-testid="timesheet-assistant-settings_button_learn-more"
          >
            <a
              href="https://support.timelyapp.com/en/articles/9997795-new-ai-magic-button"
              target="_blank"
              rel="noopener noreferrer"
            >
              <LinkExternal01 />
              Learn more
            </a>
          </Button>
          {!!props.debugMode && !!props.onSetView && (
            <Button
              tooltip="Only visible to internal users"
              size="small"
              priority="xlow"
              onClick={() => props.onSetView?.(AiPreferencesViews.Debug)}
            >
              <CustomIcon.BorderlessStar width={24} height={24} />
              Debug
            </Button>
          )}
          <Box.Spacer />
          <Button onClick={props.onClose} data-testid="timesheet-assistant-settings_button_cancel">
            Cancel
          </Button>
          <Button
            disabled={!isEnabled}
            type="submit"
            priority={isDirty || !initiallyEnabled ? 'xhigh' : 'high'}
            isLoading={isSubmitting}
            data-testid="timesheet-assistant-settings_button_save"
          >
            {initiallyEnabled ? 'Save' : 'Activate AI'}
          </Button>
        </Settings.Main.Footer>
      </Form>
    </>
  );
};
export default AiPreferencesGeneralSettings;
